import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, RouterProvider, createRootRoute, createRoute, createRouter, lazyRouteComponent, redirect } from "@tanstack/react-router";
import "@jaks/ui/tokens.css";
import "./shell/shell.css";
import { Shell } from "./shell/shell.tsx";
import { DETAIL_MODULES, LEGACY_DETAIL_REDIRECTS, LEGACY_REDIRECTS, MODULES, detailRouteOf, legacyRedirect } from "./manifest.ts";

const qc = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: true, retry: 1 } } });

const rootRoute = createRootRoute({ component: Outlet });
// The shell wraps every module screen.
const shellRoute = createRoute({ getParentRoute: () => rootRoute, id: "shell", component: Shell });
const indexRoute = createRoute({ getParentRoute: () => shellRoute, path: "/", beforeLoad: () => { throw redirect({ to: "/home" }); } });
const moduleRoutes = MODULES.map((m) =>
  createRoute({ getParentRoute: () => shellRoute, path: `/${m.id}`, component: m.component }),
);
// A record screen under a module: `/vendors/<id>`, `/sales/c/<accountId>`. A
// dynamic segment ranks above the splat, so an unknown deeper path still falls
// to not-found.
const detailRoutes = DETAIL_MODULES.map((m) =>
  createRoute({ getParentRoute: () => shellRoute, path: detailRouteOf(m), component: m.detail }),
);
// An address that used to be a section (`/customers`, T43) answers with where
// it went. A concrete route, so the splat below never sees it; `beforeLoad`
// rather than a component, so nothing renders and the history entry is the
// destination.
const redirectRoutes = LEGACY_REDIRECTS.map((r) =>
  createRoute({
    getParentRoute: () => shellRoute,
    path: r.from,
    beforeLoad: ({ location }) => {
      throw redirect({ href: legacyRedirect(r.from, location.searchStr) ?? "/home", replace: true });
    },
  }),
);
// A record address under a section that no longer exists (`/vendors/<id>`, T61):
// a screen that asks which list the record belongs on and replaces itself.
const legacyDetailRoutes = LEGACY_DETAIL_REDIRECTS.map((r) =>
  createRoute({ getParentRoute: () => shellRoute, path: r.path, component: r.component }),
);
const moreRoute = createRoute({ getParentRoute: () => shellRoute, path: "/more", component: lazyRouteComponent(() => import("./modules/more.tsx")) });
// A splat under the shell, not the router's notFound: a not-found bubbles past
// a pathless layout to the root, which would drop the sidebar. Splat ranks
// below every concrete route, so it only catches what nothing else claimed.
const notFoundRoute = createRoute({ getParentRoute: () => shellRoute, path: "$", component: lazyRouteComponent(() => import("./shell/not-found.tsx")) });
const router = createRouter({ routeTree: rootRoute.addChildren([shellRoute.addChildren([indexRoute, moreRoute, ...moduleRoutes, ...detailRoutes, ...redirectRoutes, ...legacyDetailRoutes, notFoundRoute])]) });
declare module "@tanstack/react-router" { interface Register { router: typeof router } }

// Installable app: the service worker precaches the shell; updates prompt
// rather than surprise-reload (a crew member mid-form keeps their screen).
//
// The button reloads the page ITSELF (T75). `registerSW` only reloads when
// workbox-window sees the new worker take control of a page that was already
// controlled when it loaded. A page that loaded with no controlling worker (the
// first visit, any Shift+Reload; `sw.ts` never claims clients) never hears
// `controllerchange` at all, so the click activated the new worker for every
// other tab and left this one on the old bundle with a button that did nothing
// from then on (reproduced 2026-10-25). A tab open longer than a minute also
// hears about one update twice (`installed` from another tab's check, then
// `waiting`), which stacked two identical bars. One bar, dismissable.
import { registerSW } from "virtual:pwa-register";
let updateBar: HTMLDivElement | undefined;
const updateSW = registerSW({
  onNeedRefresh() {
    if (updateBar) return;
    const bar = document.createElement("div");
    updateBar = bar;
    bar.setAttribute("role", "status");
    bar.style.cssText = "position:fixed;bottom:14px;left:50%;transform:translateX(-50%);z-index:80;background:var(--sidebar);color:#fff;padding:10px 16px;border-radius:12px;font:600 13px var(--font-sans);display:flex;gap:12px;align-items:center;box-shadow:0 8px 30px rgba(10,19,39,.35)";
    bar.innerHTML = "<span>A new version of JAKS is ready.</span>";
    const btn = document.createElement("button");
    btn.textContent = "Update now";
    btn.style.cssText = "background:var(--red-500);color:#fff;padding:6px 12px;border-radius:8px;font-weight:700";
    btn.onclick = () => {
      btn.disabled = true;
      btn.textContent = "Updating…";
      void applyUpdate();
    };
    const later = document.createElement("button");
    later.textContent = "×";
    later.setAttribute("aria-label", "Not now");
    later.style.cssText = "background:transparent;color:#fff;opacity:.75;font-size:18px;line-height:1;padding:0 4px";
    later.onclick = () => {
      bar.remove();
      updateBar = undefined;
    };
    bar.append(btn, later);
    document.body.appendChild(bar);
  },
});

/**
 * Tell the waiting worker to take over, then reload: at once when this page is
 * told it has a new controller, or on a short timer when it never will be. No
 * waiting worker means another tab already did this, and a plain reload lands
 * on the new bundle. A reload that comes too early is served by the old worker
 * and simply shows the prompt again.
 */
async function applyUpdate(): Promise<void> {
  const reload = () => window.location.reload();
  const container = navigator.serviceWorker;
  if (!container) return reload();
  container.addEventListener("controllerchange", reload, { once: true });
  const registration = await container.getRegistration().catch(() => undefined);
  if (!registration?.waiting) return reload();
  await updateSW(true);
  setTimeout(reload, 2000);
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
